import ApiService from "./ApiService";
import { stringify } from "querystring";
import { format } from "util";

const ENDPOINTS = {
  CREATE_LOYALTY_PROGRAM: "loyalty-program/",
  GET_LOYALTY_PROGRAM: "loyalty-program/settings/",
  GET_LOYALTY_PROGRAM_BASIC_STATS: "loyalty-program/statistics/",
  EDIT_LOYALTY_PROGRAM: "loyalty-program/%s/",
  CREATE_REWARD: "loyalty-program/%s/loyalty-item/",
  GET_REWARDS: "loyalty-program/%s/loyalty-item/",
  GET_REWARD_USERS: "loyalty-program/%s/loyalty-item/%s/users/",
  DELETE_REWARD: "loyalty-program/%s/loyalty-item/%s/",
  UPDATE_REWARD: "loyalty-program/%s/loyalty-item/%s/",
  CREATE_LOYALTY_LEVEL: "/loyalty-level/",
  GET_LOYALTY_LEVELS: "/loyalty-level/",
  GET_LOYALTY_LEVEL: "/loyalty-level/%s/",
  DELETE_LOYALTY_LEVEL: "/loyalty-level/%s/",
  GET_LEVEL_MEMBERS: "/loyalty-level/%s/loyalty-users/",
  REDEEM_REWARD: "/loyalty-item-redeem/",
  GET_USER_LOYALTY_PROFILE: "/loyalty-users/profile/",
  GET_SINGLE_USER_REWARDS: "/loyalty-user-items/",
  GET_NEXT_APPOINTMENT: "/loyalty-appointments/next-appointment/",
  GET_USER_ACTIVITY: "/loyalty-users/activities/",
  GET_USER_MEMBERSHIP: "/loyalty-users/memberships/",
  GET_LOYALTY_CONTACT: "/loyalty-contact/retrieve/",
  GET_LOYALTY_DOCTORS: "/loyalty-doctors/",
  GET_LOYALTY_ITEM_PERFORMANCE: "performance/loyalty-item/",
  GET_VOUCHERS_IN_CIRCULATION: "vouchers-in-circulation/",
  GET_EXPIRING_VOUCHERS: "expiring-vouchers/",
  GET_VOUCHER_TYPE_USAGE: "performance/loyalty-item/voucher-types-usage/",
  GET_TOP_USERS_BY_REDEMPTION: "performance/loyalty-item/users-by-redemptions/",
  GET_LEVEL_MEMBERS_PERFORMANCE: "performance/loyalty-item/users-by-level/",
  APPLY_VOUCHER: "performance/loyalty-item/%s/",
  GET_AVERAGE_SPENDING: "performance/loyalty-item/avg-spending-by-level/",
  GET_LEVEL_MOVEMENT: "performance/loyalty-item/level-movement/",
  GET_LOYALTY_ACTIVITY: "loyalty-program/%s/activity/",
  GET_USERS: "loyalty-user/",
  GET_SINGLE_USER: "loyalty-user/%s/",
  GET_USER_POINTS: "loyalty-points/",
  UPDATE_USER: "loyalty-user/%s/",
  GET_LOYALTY_ITEM_OFFER: "available-loyalty-items/",
  LIST_POTENTIAL_USERS: "potential-users/",
  GET_CONSENT_FORMS: "/loyalty-consent-forms/",
  SIGN_CONSENT_FORM_BY_PATIENT: "/loyalty-consent-forms/%s/update-and-sign/",
};

class LoyaltyService extends ApiService {
  createLoyaltyProgram = (payload) => {
    return this.apiClient.post(ENDPOINTS.CREATE_LOYALTY_PROGRAM, payload);
  };

  getLoyaltyProgram = () => {
    return this.apiClient.get(ENDPOINTS.GET_LOYALTY_PROGRAM);
  };

  getLoyaltyProgramBasicStats = () => {
    return this.apiClient.get(ENDPOINTS.GET_LOYALTY_PROGRAM_BASIC_STATS);
  };

  getLoyaltyItemPerformance = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_LOYALTY_ITEM_PERFORMANCE}?${stringify(payload)}`
    );
  };

  getVoucherTypeUsage = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_VOUCHER_TYPE_USAGE}?${stringify(payload)}`
    );
  };

  getVouchersInCirculation = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_VOUCHERS_IN_CIRCULATION}?${stringify(payload)}`
    );
  };

  getExpiringVouchers = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_EXPIRING_VOUCHERS}?${stringify(payload)}`
    );
  };

  getTopUsersByRedemption = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_TOP_USERS_BY_REDEMPTION}?${stringify(payload)}`
    );
  };

  getLevelMembersPerformance = () => {
    return this.apiClient.get(ENDPOINTS.GET_LEVEL_MEMBERS_PERFORMANCE);
  };

  getAverageSpending = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_AVERAGE_SPENDING}?${stringify(payload)}`
    );
  };

  getLevelMovement = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_LEVEL_MOVEMENT}?${stringify(payload)}`
    );
  };

  editLoyaltyProgram = (payload) => {
    return this.apiClient.patch(
      format(ENDPOINTS.EDIT_LOYALTY_PROGRAM, payload?.id),
      payload
    );
  };

  createReward = (payload) => {
    return this.apiClient.post(
      format(ENDPOINTS.CREATE_REWARD, payload?.loyaltyProgramId),
      payload?.data
    );
  };

  getRewards = (payload) => {
    return this.apiClient.get(
      format(
        `${ENDPOINTS.GET_REWARDS}?${stringify(payload.queryParams)}`,
        payload?.loyaltyProgramId
      )
    );
  };

  deleteReward = (payload) => {
    return this.apiClient.delete(
      format(
        ENDPOINTS.DELETE_REWARD,
        payload?.loyaltyProgramId,
        payload?.rewardId
      )
    );
  };
  updateReward = (payload) => {
    return this.apiClient.patch(
      format(
        ENDPOINTS.UPDATE_REWARD,
        payload?.loyaltyProgramId,
        payload?.rewardId
      ),
      payload?.data
    );
  };

  getRewardUsers = (payload) => {
    const queryParams = payload?.queryParams
      ? `?${stringify(payload.queryParams)}`
      : "";
    return this.apiClient.get(
      format(
        `${ENDPOINTS.GET_REWARD_USERS}${queryParams}`,
        payload?.loyaltyProgramId,
        payload?.rewardId
      )
    );
  };

  createLoyaltyLevel = (payload) => {
    return this.apiClient.post(ENDPOINTS.CREATE_LOYALTY_LEVEL, payload);
  };

  getLoyaltyLevels = (payload) => {
    const queryParams = payload?.queryParams
      ? `?${stringify(payload.queryParams)}`
      : "";
    return this.apiClient.get(`${ENDPOINTS.GET_LOYALTY_LEVELS}${queryParams}`);
  };

  getLoyaltyLevel = (levelId) => {
    return this.apiClient.get(format(ENDPOINTS.GET_LOYALTY_LEVEL, levelId));
  };

  deleteLoyaltyLevel = (levelId) => {
    return this.apiClient.delete(
      format(ENDPOINTS.DELETE_LOYALTY_LEVEL, levelId)
    );
  };

  getLevelMembers = (levelId, payload) => {
    return this.apiClient.get(
      `${format(ENDPOINTS.GET_LEVEL_MEMBERS, levelId)}?${stringify(payload)}`
    );
  };

  redeemReward = (payload) => {
    return this.apiClient.post(ENDPOINTS.REDEEM_REWARD, payload);
  };

  getUserLoyaltyProfile = () => {
    return this.apiClient.get(ENDPOINTS.GET_USER_LOYALTY_PROFILE);
  };

  getNextAppointment = () => {
    return this.apiClient.get(ENDPOINTS.GET_NEXT_APPOINTMENT);
  };

  getUserActivity = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_USER_ACTIVITY}?${stringify(payload)}`
    );
  };

  getSingleUserRewards = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_SINGLE_USER_REWARDS}?${stringify(payload)}`
    );
  };

  applyVoucher = (pk, payload) => {
    return this.apiClient.patch(format(ENDPOINTS.APPLY_VOUCHER, pk), payload);
  };

  getUserMembership = () => {
    return this.apiClient.get(ENDPOINTS.GET_USER_MEMBERSHIP);
  };

  getLoyaltyContact = () => {
    return this.apiClient.get(ENDPOINTS.GET_LOYALTY_CONTACT);
  };

  getDoctors = (payload) => {
    return this.apiClient.get(
      `${ENDPOINTS.GET_LOYALTY_DOCTORS}?${stringify(payload)}`
    );
  };

  getLoyaltyActivity = (loyaltyProgramId, payload) => {
    const queryParams = payload ? `?${stringify(payload)}` : "";
    return this.apiClient.get(
      format(
        `${ENDPOINTS.GET_LOYALTY_ACTIVITY}${queryParams}`,
        loyaltyProgramId
      )
    );
  };

  getUsers = (payload) => {
    const queryParams = payload ? `?${stringify(payload)}` : "";
    return this.apiClient.get(`${ENDPOINTS.GET_USERS}${queryParams}`);
  };

  getSingleUser = (id) => {
    return this.apiClient.get(format(ENDPOINTS.GET_SINGLE_USER, id));
  };

  getUserPoints = (payload) => {
    const queryParams = payload ? `?${stringify(payload)}` : "";
    return this.apiClient.get(`${ENDPOINTS.GET_USER_POINTS}${queryParams}`);
  };

  updateUser = (userId, payload) => {
    return this.apiClient.patch(format(ENDPOINTS.UPDATE_USER, userId), payload);
  };

  getLoyaltyItemOffer = (payload) => {
    const queryParams = payload ? `?${stringify(payload)}` : "";
    return this.apiClient.get(
      `${ENDPOINTS.GET_LOYALTY_ITEM_OFFER}${queryParams}`
    );
  };

  listPotentialUsers = (payload) => {
    const queryParams = payload ? `?${stringify(payload)}` : "";
    return this.apiClient.get(
      `${ENDPOINTS.LIST_POTENTIAL_USERS}${queryParams}`
    );
  };

  getConsentForms = (payload) => {
    const queryParams = payload ? `?${stringify(payload)}` : "";
    return this.apiClient.get(`${ENDPOINTS.GET_CONSENT_FORMS}${queryParams}`);
  };

  signConsentFormByPatient = (id, payload) =>
    this.apiClient.patch(
      format(ENDPOINTS.SIGN_CONSENT_FORM_BY_PATIENT, id),
      payload
    );
}

export default new LoyaltyService();
